import React, { FC, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { HStack } from '@montel/montelpro-shared-components-v2';
import { useMenu } from '../useMenu';
import { MenuProps } from '../menuTypes';
import { Topbar } from './Topbar';
import { StyledTopMenuGroup, TopbarMenuWrapper } from './TopMenuGroup.styles';
import KebabMenu from './kebab-menu';
import Carousel from './Carousel';
import { useObserveElementWidth } from './Carousel/hooks/useObserveElementWidth';
import TopMenuContent from './TopMenuContent';

/**
 * Top menu component which is displayed horizontally on the top of the page and serves as the secondary nav menu.
 */
const TopMenu: FC<MenuProps> = ({
	menuConfig = [],
	w = '100%',
	h = 'auto',
	bg = 'transparent',
	maxElements = 5,
	className,
	...otherProps
}) => {
	const innerRef = useRef<any>(null);

	const { items, isActiveItemByIndex } = useMenu({
		items: menuConfig,
		locationProvider: useLocation,
	});

	const splicePoint = useMemo(() => {
		let splPoint = 0;
		let numberOfClickable = 0;

		while (splPoint < items.length && numberOfClickable < maxElements) {
			if (items[splPoint].type === 'Clickable') {
				numberOfClickable += 1;
			}
			splPoint += 1;
		}

		return splPoint;
	}, [maxElements, items]);

	const visibleItems = items.slice(0, splicePoint);
	const hiddenItems = items.slice(splicePoint);
	const innerWidth = innerRef?.current?.clientWidth;

	const { width, ref } = useObserveElementWidth<HTMLDivElement>();

	return (
		<Topbar
			ref={ref}
			$w={w}
			$h={h}
			$bg={bg}
			className={className}
			{...otherProps}
		>
			<TopbarMenuWrapper ref={innerRef}>
				{width < innerWidth ? (
					<HStack align="center">
						<Carousel gap={24}>
							{visibleItems.map((item, index) => (
								<TopMenuContent
									key={index}
									item={item}
									index={index}
									isActiveItemByIndex={isActiveItemByIndex}
								/>
							))}
						</Carousel>
					</HStack>
				) : (
					<StyledTopMenuGroup>
						{visibleItems.map((item, index) => (
							<TopMenuContent
								key={index}
								item={item}
								index={index}
								isActiveItemByIndex={isActiveItemByIndex}
							/>
						))}
					</StyledTopMenuGroup>
				)}
			</TopbarMenuWrapper>
			<KebabMenu
				menuConfig={hiddenItems}
				indexOffset={splicePoint}
				isActiveItemByIndex={isActiveItemByIndex}
			/>
		</Topbar>
	);
};

export default TopMenu;
